import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Briefcase, GraduationCap, MessagesSquare, Newspaper, Palette, PartyPopper, X } from 'lucide-react';
import api from '../services/api';

const QK_ACTIVATIE_WELKOM = ['professional-activatie-welkom'] as const;

interface ActivatieWelkomStatus {
  tonen: boolean;
  getoond: boolean;
  voornaam?: string | null;
}

// ---------------------------------------------------------------------------
// Feest-engine: confetti-kanonnen, vuurwerk en confettiregen op één canvas.
// ---------------------------------------------------------------------------

const KLEUREN = ['#0B1F3A', '#2563eb', '#f59e0b', '#10b981', '#ef4444', '#ec4899', '#a855f7', '#facc15'];

type Deeltje =
  | { soort: 'confetti'; x: number; y: number; vx: number; vy: number; rot: number; vr: number; flip: number; vf: number; w: number; h: number; kleur: string; leven: number }
  | { soort: 'vonk'; x: number; y: number; px: number; py: number; vx: number; vy: number; kleur: string; leven: number; verval: number }
  | { soort: 'pijl'; x: number; y: number; vy: number; doelY: number; kleur: string; leven: number };

const willekeurig = (min: number, max: number) => min + Math.random() * (max - min);
const kleur = () => KLEUREN[Math.floor(Math.random() * KLEUREN.length)];

function maakConfetti(x: number, y: number, hoek: number, snelheid: number): Deeltje {
  return {
    soort: 'confetti', x, y,
    vx: Math.cos(hoek) * snelheid, vy: Math.sin(hoek) * snelheid,
    rot: willekeurig(0, Math.PI * 2), vr: willekeurig(-0.25, 0.25),
    flip: willekeurig(0, Math.PI * 2), vf: willekeurig(0.08, 0.2),
    w: willekeurig(6, 11), h: willekeurig(10, 16), kleur: kleur(), leven: 1,
  };
}

function useFeest(canvasRef: React.RefObject<HTMLCanvasElement | null>, actief: boolean) {
  const deeltjes = useRef<Deeltje[]>([]);
  const timers = useRef<number[]>([]);
  const frame = useRef<number | null>(null);

  const loop = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    let vorige = performance.now();

    const tik = (nu: number) => {
      const dt = Math.min((nu - vorige) / 16.67, 3);
      vorige = nu;
      const W = window.innerWidth;
      const H = window.innerHeight;
      ctx.clearRect(0, 0, W, H);

      const nieuw: Deeltje[] = [];
      for (const d of deeltjes.current) {
        if (d.soort === 'confetti') {
          d.vy += 0.32 * dt;
          d.vx *= Math.pow(0.985, dt);
          d.vy *= Math.pow(0.985, dt);
          d.x += d.vx * dt + Math.sin(d.flip) * 0.6;
          d.y += d.vy * dt;
          d.rot += d.vr * dt;
          d.flip += d.vf * dt;
          if (d.y > H + 40) continue;
          ctx.save();
          ctx.translate(d.x, d.y);
          ctx.rotate(d.rot);
          ctx.scale(1, Math.cos(d.flip));
          ctx.fillStyle = d.kleur;
          ctx.fillRect(-d.w / 2, -d.h / 2, d.w, d.h);
          ctx.restore();
        } else if (d.soort === 'pijl') {
          d.y += d.vy * dt;
          ctx.fillStyle = '#fff7cc';
          ctx.beginPath();
          ctx.arc(d.x, d.y, 2.5, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = 'rgba(255, 220, 120, 0.35)';
          ctx.fillRect(d.x - 1, d.y, 2, 18);
          if (d.y <= d.doelY) {
            const aantal = 70;
            for (let i = 0; i < aantal; i++) {
              const hoek = (i / aantal) * Math.PI * 2 + willekeurig(-0.05, 0.05);
              const v = willekeurig(2, 6.5);
              nieuw.push({ soort: 'vonk', x: d.x, y: d.y, px: d.x, py: d.y, vx: Math.cos(hoek) * v, vy: Math.sin(hoek) * v, kleur: Math.random() < 0.7 ? d.kleur : kleur(), leven: 1, verval: willekeurig(0.012, 0.022) });
            }
            continue;
          }
        } else {
          d.px = d.x;
          d.py = d.y;
          d.vx *= Math.pow(0.97, dt);
          d.vy = d.vy * Math.pow(0.97, dt) + 0.06 * dt;
          d.x += d.vx * dt;
          d.y += d.vy * dt;
          d.leven -= d.verval * dt;
          if (d.leven <= 0) continue;
          ctx.globalAlpha = Math.max(d.leven, 0);
          ctx.strokeStyle = d.kleur;
          ctx.lineWidth = 2.2;
          ctx.beginPath();
          ctx.moveTo(d.px, d.py);
          ctx.lineTo(d.x, d.y);
          ctx.stroke();
          ctx.globalAlpha = 1;
        }
        nieuw.push(d);
      }
      deeltjes.current = nieuw;

      if (deeltjes.current.length > 0 || timers.current.length > 0) {
        frame.current = requestAnimationFrame(tik);
      } else {
        frame.current = null;
      }
    };
    frame.current = requestAnimationFrame(tik);
  }, [canvasRef]);

  const start = useCallback(() => {
    if (frame.current === null) loop();
  }, [loop]);

  const kanon = useCallback((zijde: 'links' | 'rechts') => {
    const W = window.innerWidth;
    const H = window.innerHeight;
    const x = zijde === 'links' ? 0 : W;
    const basis = zijde === 'links' ? -Math.PI / 3 : (-2 * Math.PI) / 3;
    const kracht = Math.sqrt(H) * 0.75;
    for (let i = 0; i < 90; i++) {
      deeltjes.current.push(maakConfetti(x, H, basis + willekeurig(-0.3, 0.3), kracht * willekeurig(0.6, 1.15)));
    }
    start();
  }, [start]);

  const vuurpijl = useCallback(() => {
    const W = window.innerWidth;
    const H = window.innerHeight;
    deeltjes.current.push({ soort: 'pijl', x: willekeurig(W * 0.15, W * 0.85), y: H, vy: -willekeurig(11, 15), doelY: willekeurig(H * 0.12, H * 0.4), kleur: kleur(), leven: 1 });
    start();
  }, [start]);

  const regen = useCallback(() => {
    const W = window.innerWidth;
    for (let i = 0; i < 120; i++) {
      const d = maakConfetti(willekeurig(0, W), willekeurig(-200, -10), Math.PI / 2, willekeurig(0.5, 2));
      deeltjes.current.push(d);
    }
    start();
  }, [start]);

  const plan = useCallback((ms: number, fn: () => void) => {
    const id = window.setTimeout(() => {
      timers.current = timers.current.filter((t) => t !== id);
      fn();
    }, ms);
    timers.current.push(id);
  }, []);

  /** Het volledige feest: kanonnen links/rechts, vuurwerkshow en tot slot confettiregen. */
  const vier = useCallback(() => {
    kanon('links');
    kanon('rechts');
    plan(350, () => { kanon('links'); kanon('rechts'); });
    [600, 1000, 1350, 1800, 2300, 2700, 3200, 3600].forEach((ms) => plan(ms, vuurpijl));
    plan(2400, regen);
  }, [kanon, vuurpijl, regen, plan]);

  // Canvas bestaat pas zodra het scherm open is; daarom op `actief` opnieuw schalen.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!actief || !canvas) return;
    const schaal = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.getContext('2d')?.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    schaal();
    window.addEventListener('resize', schaal);
    return () => {
      window.removeEventListener('resize', schaal);
      timers.current.forEach((t) => window.clearTimeout(t));
      timers.current = [];
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      frame.current = null;
      deeltjes.current = [];
    };
  }, [canvasRef, actief]);

  return vier;
}

// ---------------------------------------------------------------------------
// Voortgangsring die van 0 naar 100% vult.
// ---------------------------------------------------------------------------

const ActiefRing: React.FC<{ label: string; animeer: boolean }> = ({ label, animeer }) => {
  const [pct, setPct] = useState(animeer ? 0 : 100);
  useEffect(() => {
    if (!animeer) return;
    const begin = performance.now();
    let id = 0;
    const tik = (nu: number) => {
      const p = Math.min((nu - begin) / 1400, 1);
      setPct(Math.round(100 * (1 - Math.pow(1 - p, 3))));
      if (p < 1) id = requestAnimationFrame(tik);
    };
    id = requestAnimationFrame(tik);
    return () => cancelAnimationFrame(id);
  }, [animeer]);

  const r = 42;
  const omtrek = 2 * Math.PI * r;
  return (
    <div className="relative h-20 w-20 shrink-0 sm:h-28 sm:w-28">
      <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
        <circle cx="50" cy="50" r={r} fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="8" />
        <circle cx="50" cy="50" r={r} fill="none" stroke="#facc15" strokeWidth="8" strokeLinecap="round" strokeDasharray={omtrek} strokeDashoffset={omtrek * (1 - pct / 100)} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
        <span className="text-xl font-extrabold tabular-nums leading-none sm:text-2xl">{pct}%</span>
        <span className="mt-1 text-[10px] font-semibold sm:text-[11px] uppercase tracking-wider text-yellow-200">{label}</span>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Het welkomst- & activatiescherm
// ---------------------------------------------------------------------------

/**
 * Eenmalig feestscherm voor de professional zodra het onboarding-contract
 * getekend is én de Stripe-koppeling geverifieerd is. De server onthoudt dat
 * het getoond is (`professional_profiles.welkom_getoond_at`). Stripe verifieert
 * asynchroon, dus zolang het scherm nog niet getoond is wordt de status
 * periodiek en bij terugkeer naar het tabblad opnieuw opgevraagd.
 */
export const ActivatieWelkomScherm: React.FC<{ enabled: boolean }> = ({ enabled }) => {
  const { t } = useTranslation('professional');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const ctaRef = useRef<HTMLButtonElement>(null);
  const [gesloten, setGesloten] = useState(false);
  const minderBeweging = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const { data } = useQuery({
    queryKey: QK_ACTIVATIE_WELKOM,
    queryFn: async (): Promise<ActivatieWelkomStatus> => (await api.get('/professional/activatie-welkom')).data?.data,
    enabled,
    refetchOnWindowFocus: true,
    refetchInterval: (q) => (q.state.data?.getoond ? false : 60_000),
  });

  const { mutate: markeerGezien } = useMutation({
    mutationFn: () => api.post('/professional/activatie-welkom/gezien'),
    onSettled: () => queryClient.invalidateQueries({ queryKey: QK_ACTIVATIE_WELKOM }),
  });

  const open = enabled && !!data?.tonen && !gesloten;
  const vier = useFeest(canvasRef, open);

  useEffect(() => {
    if (!open) return;
    ctaRef.current?.focus();
    if (minderBeweging) return;
    const id = window.setTimeout(vier, 150);
    return () => window.clearTimeout(id);
  }, [open, vier, minderBeweging]);

  const sluit = useCallback((naar?: string) => {
    setGesloten(true);
    markeerGezien();
    if (naar) navigate(naar);
  }, [markeerGezien, navigate]);

  useEffect(() => {
    if (!open) return;
    const opEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') sluit(); };
    window.addEventListener('keydown', opEsc);
    return () => window.removeEventListener('keydown', opEsc);
  }, [open, sluit]);

  if (!open) return null;

  const stappen = [
    { icon: Briefcase, titel: t('activatieWelkom.stapOpdrachtenTitel'), tekst: t('activatieWelkom.stapOpdrachtenTekst'), naar: '/opdrachten', tint: 'bg-blue-50 text-[#0B1F3A]' },
    { icon: Palette, titel: t('activatieWelkom.stapPortfolioTitel'), tekst: t('activatieWelkom.stapPortfolioTekst'), naar: '/professional/profile', tint: 'bg-pink-50 text-pink-600' },
    { icon: MessagesSquare, titel: t('activatieWelkom.stapCommunityTitel'), tekst: t('activatieWelkom.stapCommunityTekst'), naar: '/community', tint: 'bg-emerald-50 text-emerald-600' },
    { icon: Newspaper, titel: t('activatieWelkom.stapNewsroomTitel'), tekst: t('activatieWelkom.stapNewsroomTekst'), naar: '/newsroom', tint: 'bg-amber-50 text-amber-600' },
    { icon: GraduationCap, titel: t('activatieWelkom.stapAcademyTitel'), tekst: t('activatieWelkom.stapAcademyTekst'), naar: '/opleidingen', tint: 'bg-violet-50 text-violet-600' },
  ];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6">
      <style>{`
        @keyframes belagom-welkom-in { from { opacity: 0; transform: translateY(24px) scale(.96); } to { opacity: 1; transform: none; } }
        @keyframes belagom-stempel { 0% { opacity: 0; transform: rotate(-14deg) scale(2.4); } 60% { opacity: 1; transform: rotate(-14deg) scale(.92); } 100% { opacity: 1; transform: rotate(-14deg) scale(1); } }
        @keyframes belagom-stap-in { from { opacity: 0; transform: translateX(-12px); } to { opacity: 1; transform: none; } }
        @media (prefers-reduced-motion: reduce) { .belagom-anim { animation: none !important; opacity: 1 !important; } }
      `}</style>

      <div className="absolute inset-0 bg-[#001a4d]/70 backdrop-blur-sm" aria-hidden="true" />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="activatie-welkom-kop"
        className="belagom-anim relative flex max-h-[94vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        style={{ animation: 'belagom-welkom-in .5s cubic-bezier(.2,.8,.2,1) both' }}
      >
        {/* Feestelijke kop */}
        <div className="relative shrink-0 overflow-hidden bg-gradient-to-br from-[#0B1F3A] via-[#1d4ed8] to-[#7c3aed] px-5 py-5 sm:px-8 sm:py-7">
          <button
            type="button"
            onClick={() => sluit()}
            aria-label={t('activatieWelkom.sluiten')}
            className="absolute right-3 top-3 rounded-full p-1.5 text-white/80 hover:bg-white/15 hover:text-white"
          >
            <X size={20} />
          </button>
          <div className="flex flex-col items-center gap-3 text-center sm:flex-row sm:gap-5 sm:text-left">
            <ActiefRing label={t('activatieWelkom.ringLabel')} animeer={!minderBeweging} />
            <div className="min-w-0">
              <h2 id="activatie-welkom-kop" className="text-xl font-extrabold leading-tight text-white sm:text-3xl">
                <span aria-hidden="true">🎉 </span>
                {data?.voornaam ? t('activatieWelkom.kop', { naam: data.voornaam }) : t('activatieWelkom.kopZonderNaam')}
              </h2>
              <p className="mt-2 text-sm text-blue-100 sm:text-base">{t('activatieWelkom.subkop')}</p>
              <span
                className="belagom-anim pointer-events-none mt-3 hidden rounded-md border-[3px] border-yellow-300 px-3 py-0.5 text-xs font-black uppercase tracking-[0.2em] text-yellow-300 sm:inline-block"
                style={{ animation: 'belagom-stempel .55s 1.5s cubic-bezier(.3,1.6,.5,1) both' }}
                aria-hidden="true"
              >
                {t('activatieWelkom.stempel')}
              </span>
            </div>
          </div>
        </div>

        {/* Inhoud */}
        <div className="overflow-y-auto px-6 py-6 sm:px-8">
          <p className="font-semibold text-gray-900">{t('activatieWelkom.aanhef')}</p>
          <p className="mt-2 text-sm leading-relaxed text-gray-600">{t('activatieWelkom.body1')}</p>
          <p className="mt-3 text-sm font-medium leading-relaxed text-gray-800">{t('activatieWelkom.body2')}</p>

          <h3 className="mt-6 flex items-center gap-2 text-base font-bold text-gray-900">
            <span aria-hidden="true">🚀</span> {t('activatieWelkom.stappenTitel')}
          </h3>
          <ul className="mt-3 space-y-2">
            {stappen.map(({ icon: Icon, titel, tekst, naar, tint }, i) => (
              <li key={naar} className="belagom-anim" style={{ animation: `belagom-stap-in .4s ${0.5 + i * 0.12}s ease-out both` }}>
                <button
                  type="button"
                  onClick={() => sluit(naar)}
                  className="group flex w-full items-start gap-3 rounded-xl border border-gray-200 p-3 text-left transition hover:border-[#0B1F3A]/40 hover:bg-blue-50/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0B1F3A]"
                >
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${tint}`}>
                    <Icon size={18} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-gray-900">{titel}</span>
                    <span className="mt-0.5 block text-xs leading-relaxed text-gray-600">{tekst}</span>
                  </span>
                  <ArrowRight size={16} className="mt-1 shrink-0 text-gray-300 transition group-hover:translate-x-0.5 group-hover:text-[#0B1F3A]" />
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Afsluiting */}
        <div className="flex shrink-0 flex-col-reverse items-stretch gap-3 border-t border-gray-100 bg-gray-50 px-6 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-8">
          {!minderBeweging && (
            <button
              type="button"
              onClick={vier}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-[#0B1F3A]"
            >
              <PartyPopper size={16} /> {t('activatieWelkom.nogEenKeer')}
            </button>
          )}
          <button
            ref={ctaRef}
            type="button"
            onClick={() => sluit('/professional/profile')}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#0B1F3A] px-6 py-3 text-base font-bold text-white shadow-lg shadow-blue-900/20 transition hover:bg-[#00256b] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#0B1F3A]/30 sm:ml-auto"
          >
            {t('activatieWelkom.cta')} <ArrowRight size={18} />
          </button>
        </div>
      </div>

      <canvas ref={canvasRef} className="pointer-events-none fixed inset-0 h-full w-full" aria-hidden="true" />
    </div>
  );
};
